import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

import api from '../services/api';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Legacy shape only. Songs submitted before the admin-managed `instruments`
// table existed stored { type: 'acoustic_guitar' }; current rows store
// { instrument_id, amp_type, other_text } and resolve names via the API.
const LEGACY_INSTRUMENT_LABELS = {
  acoustic_guitar: 'Acoustic Guitar',
  electric_guitar: 'Electric Guitar',
  keyboard:        'Keyboard / Piano',
  other_miced:     'Other (Mic)',
  other_di:        'Other (DI)',
};

/**
 * id -> name for every instrument (including inactive ones — a song may
 * reference an instrument that has since been retired). Fetched once per
 * page load; failure degrades to "Instrument #<id>" rather than blanking
 * the column.
 */
let _instrumentNamesPromise = null;

function loadInstrumentNames() {
  if (!_instrumentNamesPromise) {
    _instrumentNamesPromise = api.get('/api/admin/instruments/all')
      .then((res) => {
        const map = {};
        for (const inst of res.data.instruments ?? []) map[String(inst.id)] = inst.name;
        return map;
      })
      .catch(() => {
        _instrumentNamesPromise = null;  // let the next attempt retry
        return {};
      });
  }
  return _instrumentNamesPromise;
}

/**
 * Human-readable line for one stored instrument entry, e.g.
 *   "Acoustic Guitar (DI)"  ·  "Cajón (MIC)"  ·  "Drums (Acoustic)"
 * The sound engineer needs the amplification, and for "Other" the
 * contestant's free text is the only useful name.
 */
function formatInstrument(inst, names) {
  if (inst == null) return '';

  let name;
  if (inst.type) {
    name = LEGACY_INSTRUMENT_LABELS[inst.type] || inst.type;
  } else {
    // Most rows snapshot instrument_name at submission time; older ones
    // carry only the FK, so fall back to the live instruments table.
    name = inst.instrument_name || names[String(inst.instrument_id)];
    const other = (inst.other_text || '').trim();
    if (name === 'Other' || !name) {
      // "Other" alone tells the engineer nothing — prefer what was typed
      name = other || name || `Instrument #${inst.instrument_id}`;
    } else if (other) {
      name = `${name} — ${other}`;
    }
  }

  const amp = (inst.amp_type || '').trim();
  return amp ? `${name} (${amp})` : name;
}

function slugify(str) {
  return (str || '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function heatFileStem(heat) {
  return slugify(`Heat-${heat.heat_number}-${heat.heat_name}`);
}

function formatDate(dateStr) {
  if (!dateStr) return null;
  // heat_date comes back as an ISO string from the DB; parse it as UTC midnight
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-AU', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * Some contestants paste lyrics with a blank line after every single line, which
 * doubles the page count for no benefit. Detect that pattern and collapse it,
 * keeping runs of two or more blank lines as genuine stanza breaks.
 *
 * Deliberately conservative: a lyric laid out normally (a blank line between
 * verses only) is left exactly as submitted.
 */
function collapseDoubleSpacing(lines) {
  let content = 0;
  let singleBlanks = 0;

  for (let i = 0; i < lines.length; i++) {
    if (lines[i].trim() !== '') { content++; continue; }
    const runStart = i;
    while (i + 1 < lines.length && lines[i + 1].trim() === '') i++;
    if (i - runStart + 1 === 1) singleBlanks++;
  }

  // Real double spacing puts a blank after nearly every line. Normal stanza
  // breaks sit far below this — the widest observed gap is 28% vs 98%.
  if (!(content > 4 && singleBlanks >= content * 0.6)) return lines;

  const out = [];
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].trim() !== '') { out.push(lines[i]); continue; }
    let run = 0;
    while (i < lines.length && lines[i].trim() === '') { run++; i++; }
    i--;
    if (run >= 2) out.push('');   // stanza break survives; the doubling does not
  }
  return out;
}

// ---------------------------------------------------------------------------
// Part 1: Lyrics PDF
// ---------------------------------------------------------------------------

/**
 * Generate and download a lyrics PDF for the given heat.
 * @param {object} heat  – heat object from GET /api/admin/heats
 * @param {{ onStart?: () => void, onDone?: () => void }} opts
 */
export function downloadLyricsPdf(heat, { onStart, onDone } = {}) {
  onStart?.();

  const doc      = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW    = doc.internal.pageSize.getWidth();
  const pageH    = doc.internal.pageSize.getHeight();
  const margin   = 20;
  const contentW = pageW - margin * 2;

  // ── Cover header (first page) ─────────────────────────────────────────────
  let y = margin;

  // SCALA branding
  doc.setFontSize(9);
  doc.setTextColor(140, 140, 160);
  doc.setFont('helvetica', 'normal');
  doc.text('SCALA Song Competition', margin, y);
  y += 9;

  // Heat name
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(20, 20, 40);
  const heatNameLines = doc.splitTextToSize(heat.heat_name, contentW);
  doc.text(heatNameLines, margin, y);
  y += heatNameLines.length * 9 + 2;

  // Date + venue
  const metaParts = [];
  const dateStr = formatDate(heat.heat_date);
  if (dateStr) metaParts.push(dateStr);
  if (heat.venue_name) metaParts.push(heat.venue_name);

  if (metaParts.length > 0) {
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 110);
    doc.text(metaParts.join('  ·  '), margin, y);
    y += 8;
  }

  // Divider
  doc.setDrawColor(200, 200, 210);
  doc.line(margin, y, pageW - margin, y);
  y += 12;

  // ── One section per song ──────────────────────────────────────────────────
  heat.songs.forEach((song, idx) => {
    if (idx > 0) {
      doc.addPage();
      y = margin;
    }

    // Song title
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(20, 20, 40);
    const titleLines = doc.splitTextToSize(song.title || '(Untitled)', contentW);
    doc.text(titleLines, margin, y);
    y += titleLines.length * 9 + 2;

    // Artist / performer
    if (song.artist) {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 110);
      doc.text(song.artist, margin, y);
      y += 8;
    }

    // Sub-divider
    doc.setDrawColor(220, 220, 230);
    doc.line(margin, y, pageW - margin, y);
    y += 9;

    // Lyrics body
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(30, 30, 30);

    const lyricsRaw   = (song.lyrics_text || '').trim();
    const lyricsText  = lyricsRaw || '[No lyrics submitted]';

    const rawLines = collapseDoubleSpacing(lyricsText.split('\n'));

    // Wrap everything up front so the true line count is known before drawing —
    // the leading below cannot be chosen without it.
    const wrappedLines = [];
    rawLines.forEach((rawLine) => {
      if (rawLine.trim() === '') wrappedLines.push('');           // stanza break
      else doc.splitTextToSize(rawLine, contentW).forEach((l) => wrappedLines.push(l));
    });

    // Widow control: a lyric that runs a line or two past the bottom used to
    // strand them alone on a fresh page. Tightening the leading just enough to
    // save that page reads far better. Only applied when the last page really
    // is nearly empty — a lyric that fills its final page is left alone rather
    // than cramped for no gain.
    const LINE_H_DEFAULT = 6.5;   // mm per line at 11pt
    const LINE_H_MIN     = 5.2;   // below this the verse starts to look cramped
    const ORPHAN_MAX     = 4;     // lines on the last page that count as stranded

    const firstAvail = (pageH - margin) - y;      // room under this song's header
    const fullAvail  = pageH - margin * 2;        // room on a continuation page

    // How the lyric divides into pages at a given leading. Uses the same
    // arithmetic as the drawing loop below, so the two cannot disagree.
    const paginate = (h) => {
      const onFirst = Math.max(0, Math.floor(firstAvail / h));
      if (wrappedLines.length <= onFirst) {
        return { pages: 1, lastPage: wrappedLines.length };
      }
      const perPage = Math.max(1, Math.floor(fullAvail / h));
      const rest    = wrappedLines.length - onFirst;
      const extra   = Math.ceil(rest / perPage);
      return { pages: 1 + extra, lastPage: rest - (extra - 1) * perPage };
    };

    let lineH = LINE_H_DEFAULT;
    const atDefault = paginate(LINE_H_DEFAULT);
    if (atDefault.pages > 1 && atDefault.lastPage <= ORPHAN_MAX) {
      for (let h = LINE_H_DEFAULT - 0.05; h >= LINE_H_MIN; h -= 0.05) {
        if (paginate(h).pages < atDefault.pages) { lineH = h; break; }
      }
    }

    wrappedLines.forEach((line) => {
      // 0.001mm slack absorbs floating-point drift so the last line of a page
      // is not pushed over by a rounding error paginate() did not see.
      if (y + lineH > pageH - margin + 0.001) {
        doc.addPage();
        y = margin;
      }
      doc.text(line, margin, y);
      y += lineH;
    });
  });

  doc.save(`${heatFileStem(heat)}-Lyrics.pdf`);
  onDone?.();
}

// ---------------------------------------------------------------------------
// Part 2: Technical Requirements PDF
// ---------------------------------------------------------------------------

/**
 * Generate and download a technical requirements PDF for the given heat.
 * @param {object} heat  – heat object from GET /api/admin/heats
 * @param {{ onStart?: () => void, onDone?: () => void }} opts
 */
export async function downloadTechPdf(heat, { onStart, onDone } = {}) {
  onStart?.();
  try {
    const doc = await buildTechPdfDoc(heat);
    doc.save(`${heatFileStem(heat)}-Tech-Requirements.pdf`);
  } finally {
    onDone?.();
  }
}

/**
 * Technical requirements PDF as a base64 string (no download). Attached to the
 * stage hand notification, which is the one email whose reader works from this
 * sheet on the night. Same document as the download, so the two never differ.
 */
export async function getTechPdfBase64(heat) {
  const doc = await buildTechPdfDoc(heat);
  return doc.output('datauristring').split(',')[1];
}

async function buildTechPdfDoc(heat) {
  // Instrument names live in the `instruments` table; songs store only the id
  const instrumentNames = await loadInstrumentNames();

  const doc    = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW  = doc.internal.pageSize.getWidth();
  const pageH  = doc.internal.pageSize.getHeight();
  const margin = 15;

  let y = margin;

  // ── Header ────────────────────────────────────────────────────────────────
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(140, 140, 160);
  doc.text('SCALA Song Competition — Technical Requirements', margin, y);
  y += 9;

  // Heat name
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(20, 20, 40);
  doc.text(heat.heat_name, margin, y);
  y += 8;

  // Date / venue / address
  const metaParts = [];
  const dateStr = formatDate(heat.heat_date);
  if (dateStr) metaParts.push(dateStr);
  if (heat.venue_name)    metaParts.push(heat.venue_name);
  if (heat.venue_address) metaParts.push(heat.venue_address);

  if (metaParts.length > 0) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 110);
    // Wrap if venue address makes the line long
    const metaStr   = metaParts.join('  ·  ');
    const metaLines = doc.splitTextToSize(metaStr, pageW - margin * 2);
    doc.text(metaLines, margin, y);
    y += metaLines.length * 5 + 4;
  }

  y += 3;

  // ── Table ─────────────────────────────────────────────────────────────────
  const rows = heat.songs.map((s) => {
    const instArr = Array.isArray(s.instruments) ? s.instruments : [];
    const labels  = instArr
      .map((i) => formatInstrument(i, instrumentNames))
      .filter((label) => label !== '');
    const instStr = labels.length > 0 ? labels.join(', ') : '—';

    return [
      s.title  || '—',
      s.artist || '—',
      s.num_performers != null ? String(s.num_performers) : '—',
      s.num_vocal_mics != null ? String(s.num_vocal_mics) : '—',
      instStr,
    ];
  });

  autoTable(doc, {
    startY:      y,
    head:        [['Song Title', 'Artist / Performer', 'Performers', 'Vocal Mics', 'Instruments']],
    body:        rows,
    margin:      { left: margin, right: margin },
    // 4mm padding gave 12.7mm rows, so a 16-act heat ran to three pages for no
    // good reason. 2.5mm keeps it readable at roughly two-thirds the height.
    styles:      { fontSize: 10, cellPadding: 2.5, valign: 'middle', overflow: 'linebreak' },
    headStyles:  { fillColor: [26, 26, 46], textColor: 255, fontStyle: 'bold', fontSize: 10 },
    alternateRowStyles: { fillColor: [245, 245, 250] },
    columnStyles: {
      0: { cellWidth: 65 },
      1: { cellWidth: 60 },
      2: { cellWidth: 24, halign: 'center' },
      3: { cellWidth: 24, halign: 'center' },
      4: { cellWidth: 'auto' },
    },
    didDrawPage: () => {
      // Footer on every page
      const footerY = pageH - 8;
      doc.setFontSize(8);
      doc.setTextColor(160, 160, 170);
      doc.text(
        'Generated by SCALA Song Competition Management System',
        margin,
        footerY,
      );
    },
  });

  return doc;
}

// ---------------------------------------------------------------------------
// Shared helpers for branding
// ---------------------------------------------------------------------------

const NAVY  = [26, 26, 46];
const GOLD  = [180, 130, 20];
const GREY  = [100, 100, 110];
const LGREY = [160, 160, 170];

/**
 * Draw inline "SCALA Song Competition" logo text at (x, y).
 * Returns the y value unchanged (caller advances y).
 */
function drawScalaLogo(doc, x, y, scalaPt = 18, tagPt = 11) {
  doc.setFontSize(scalaPt);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...GOLD);
  doc.text('SCALA', x, y);
  const scalaW = doc.getTextWidth('SCALA');

  doc.setFontSize(tagPt);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...NAVY);
  doc.text(' Song Competition', x + scalaW, y);
}

/**
 * Width of the two-tone "SCALA Song Competition" block, so it can be
 * right-aligned. The parts use different sizes and weights, so the width has to
 * be measured under each before it can be summed.
 */
function scalaLogoWidth(doc, scalaPt = 18, tagPt = 11) {
  doc.setFontSize(scalaPt);
  doc.setFont('helvetica', 'bold');
  const a = doc.getTextWidth('SCALA');
  doc.setFontSize(tagPt);
  doc.setFont('helvetica', 'normal');
  return a + doc.getTextWidth(' Song Competition');
}

function heatYear(heat) {
  if (heat.heat_date) return new Date(heat.heat_date + 'T00:00:00Z').getUTCFullYear();
  return new Date().getFullYear();
}

// ---------------------------------------------------------------------------
// Part 3: Running Sheet PDF
// ---------------------------------------------------------------------------

/**
 * Generate and download a running sheet PDF for the given heat.
 * Single A4 portrait page with a two-column header (branding left,
 * heat details right) and a bordered running-order table.
 */
export function downloadRunningSheetPdf(heat, { onStart, onDone } = {}) {
  onStart?.();

  const doc    = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW  = doc.internal.pageSize.getWidth();
  const pageH  = doc.internal.pageSize.getHeight();
  const margin = 15;

  const year    = heatYear(heat);
  const dateStr = formatDate(heat.heat_date);

  // ── Left column: branding ─────────────────────────────────────────────────
  let leftY = margin;

  drawScalaLogo(doc, margin, leftY, 20, 13);
  leftY += 8;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(...GREY);
  doc.text(`Festival Of Original Music ${year}`, margin, leftY);
  leftY += 6;

  // ── Right column: heat details ────────────────────────────────────────────
  const rightX = pageW - margin;
  let rightY   = margin;

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...NAVY);
  doc.text(heat.heat_name, rightX, rightY, { align: 'right' });
  rightY += 7;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GREY);

  if (dateStr) {
    doc.text(dateStr, rightX, rightY, { align: 'right' });
    rightY += 5;
  }
  if (heat.performer_arrival_time) {
    doc.text(`Performer arrival: ${heat.performer_arrival_time.slice(0, 5)}`, rightX, rightY, { align: 'right' });
    rightY += 5;
  }
  if (heat.venue_name) {
    doc.text(heat.venue_name, rightX, rightY, { align: 'right' });
    rightY += 5;
  }
  if (heat.venue_address) {
    const addrLines = doc.splitTextToSize(heat.venue_address, 100);
    addrLines.forEach((line) => {
      doc.text(line, rightX, rightY, { align: 'right' });
      rightY += 5;
    });
  }

  // ── Divider line ──────────────────────────────────────────────────────────
  let y = Math.max(leftY, rightY) + 4;

  doc.setDrawColor(...NAVY);
  doc.setLineWidth(0.6);
  doc.line(margin, y, pageW - margin, y);
  y += 8;

  // ── Running order table ───────────────────────────────────────────────────
  // Breaks are stored against a position, so they appear as their own row after
  // that performer. The MC works from this sheet on the night, so a break that
  // only existed on the admin screen would be no use at all.
  const breaks = new Set((heat.breaks || []).map(Number));
  const rows = [];
  const breakRowIndexes = [];
  heat.songs.forEach((s, idx) => {
    rows.push([String(idx + 1), s.artist || '—', s.title || '—']);
    if (breaks.has(idx + 1)) {
      breakRowIndexes.push(rows.length);
      rows.push(['', 'BREAK', '']);
    }
  });

  autoTable(doc, {
    startY:   y,
    head:     [['#', 'Artist / Band', 'Song Title']],
    body:     rows,
    didParseCell: (data) => {
      if (data.section === 'body' && breakRowIndexes.includes(data.row.index)) {
        data.cell.styles.fillColor = [254, 243, 199];
        data.cell.styles.textColor = [146, 64, 14];
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.halign    = 'center';
      }
    },
    margin:   { left: margin, right: margin },
    // cellPadding was 4, leaving a 12mm '#' column only 4mm of usable width —
    // just under what "10" needs at 11pt, so double-digit positions wrapped.
    styles:      { fontSize: 11, cellPadding: 2.5, valign: 'middle' },
    headStyles:  { fillColor: NAVY, textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [245, 245, 250] },
    columnStyles: {
      0: { cellWidth: 14, halign: 'center', fontStyle: 'bold' },
      1: { cellWidth: 80 },
      2: { cellWidth: 'auto' },
    },
    didDrawPage: () => {
      const footerY = pageH - 8;
      doc.setFontSize(8);
      doc.setTextColor(...LGREY);
      doc.text('Produced by SCALA Song Competition Management System', margin, footerY);
      doc.text(
        new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }),
        pageW - margin,
        footerY,
        { align: 'right' },
      );
    },
  });

  doc.save(`${heatFileStem(heat)}-Running-Sheet.pdf`);
  onDone?.();
}

/**
 * Generate a running sheet PDF and return it as a base64 string (no download).
 * Used by the notify-judges / notify-performers flows.
 */
export function getRunningSheetPdfBase64(heat) {
  const doc    = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW  = doc.internal.pageSize.getWidth();
  const pageH  = doc.internal.pageSize.getHeight();
  const margin = 15;

  const year    = heatYear(heat);
  const dateStr = formatDate(heat.heat_date);

  let leftY = margin;
  drawScalaLogo(doc, margin, leftY, 20, 13);
  leftY += 8;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(...GREY);
  doc.text(`Festival Of Original Music ${year}`, margin, leftY);

  const rightX = pageW - margin;
  let rightY   = margin;
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...NAVY);
  doc.text(heat.heat_name, rightX, rightY, { align: 'right' });
  rightY += 7;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GREY);
  if (dateStr) { doc.text(dateStr, rightX, rightY, { align: 'right' }); rightY += 5; }
  if (heat.performer_arrival_time) { doc.text(`Performer arrival: ${heat.performer_arrival_time.slice(0, 5)}`, rightX, rightY, { align: 'right' }); rightY += 5; }
  if (heat.venue_name) { doc.text(heat.venue_name, rightX, rightY, { align: 'right' }); rightY += 5; }
  if (heat.venue_address) {
    const addrLines = doc.splitTextToSize(heat.venue_address, 100);
    addrLines.forEach((line) => { doc.text(line, rightX, rightY, { align: 'right' }); rightY += 5; });
  }

  let y = Math.max(leftY + 6, rightY) + 4;
  doc.setDrawColor(...NAVY);
  doc.setLineWidth(0.6);
  doc.line(margin, y, pageW - margin, y);
  y += 8;

  // Same break handling as the downloadable sheet — this is the copy that is
  // emailed to judges, performers and the MC, so it must match.
  const breaks = new Set((heat.breaks || []).map(Number));
  const rows = [];
  const breakRowIndexes = [];
  heat.songs.forEach((s, idx) => {
    rows.push([String(idx + 1), s.artist || '—', s.title || '—']);
    if (breaks.has(idx + 1)) {
      breakRowIndexes.push(rows.length);
      rows.push(['', 'BREAK', '']);
    }
  });

  autoTable(doc, {
    startY:   y,
    head:     [['#', 'Artist / Band', 'Song Title']],
    body:     rows,
    didParseCell: (data) => {
      if (data.section === 'body' && breakRowIndexes.includes(data.row.index)) {
        data.cell.styles.fillColor = [254, 243, 199];
        data.cell.styles.textColor = [146, 64, 14];
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.halign    = 'center';
      }
    },
    margin:   { left: margin, right: margin },
    // cellPadding was 4, leaving a 12mm '#' column only 4mm of usable width —
    // just under what "10" needs at 11pt, so double-digit positions wrapped.
    styles:      { fontSize: 11, cellPadding: 2.5, valign: 'middle' },
    headStyles:  { fillColor: NAVY, textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [245, 245, 250] },
    columnStyles: {
      0: { cellWidth: 14, halign: 'center', fontStyle: 'bold' },
      1: { cellWidth: 80 },
      2: { cellWidth: 'auto' },
    },
    didDrawPage: () => {
      const footerY = pageH - 8;
      doc.setFontSize(8);
      doc.setTextColor(...LGREY);
      doc.text('Produced by SCALA Song Competition Management System', margin, footerY);
      doc.text(
        new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }),
        pageW - margin, footerY, { align: 'right' },
      );
    },
  });

  // Return as base64 string (no 'data:...' prefix)
  return doc.output('datauristring').split(',')[1];
}

// ---------------------------------------------------------------------------
// Part 4: People's Choice Voting Slip PDF
// ---------------------------------------------------------------------------

/** Fetch an image URL and return a base64 data URL for jsPDF. */
async function loadImageDataUrl(url) {
  const response = await fetch(url);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Generate and download a people's choice voting slip PDF for the given heat.
 * Landscape A4 (297 × 210 mm), divided by a vertical dashed cut line at the
 * centre. Each half is an A5 portrait slip (148.5 × 210 mm) containing:
 *   - SCALA logo (left) + header text (right)
 *   - Heat name / date heading
 *   - Numbered voting table (#, Name, Song Title, Vote)
 *   - Voting instructions footer
 * The ✂ symbol sits at the top of the vertical cut line.
 */
export async function downloadVotingSlipPdf(heat, { onStart, onDone } = {}) {
  onStart?.();

  // Fetch the SCALA logo from the server's static /assets route.
  // Aspect ratio: 572 × 783 px → height = logoW × (783/572)
  const LOGO_W = 22;                       // mm
  const LOGO_H = LOGO_W * (783 / 572);     // ≈ 30.1 mm

  let logoDataUrl = null;
  try {
    logoDataUrl = await loadImageDataUrl('/assets/scala-logo.png');
  } catch {
    // Logo unavailable — fall back to text-only header
  }

  // A5 portrait: 148 × 210 mm. One slip per page.
  const doc   = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a5' });
  const pageW = doc.internal.pageSize.getWidth();   // 148
  const pageH = doc.internal.pageSize.getHeight();  // 210
  const M     = 10; // page margin

  const contentX = M;
  const contentW = pageW - M * 2;  // 128 mm
  const marginR  = M;

  const year      = heatYear(heat);
  const dateShort = heat.heat_date
    ? new Date(heat.heat_date + 'T00:00:00Z').toLocaleDateString('en-AU', {
        day: 'numeric', month: 'long', timeZone: 'UTC',
      })
    : '';

  const INSTRUCTIONS =
    'To vote, place your favourite three acts in order of preference — 1 being your favourite, ' +
    '2 your second and 3 your third. After the final performer has presented their song and you have ' +
    'completed your voting form, please hand it to the SCALA team on the door who will tally the votes.';

  // ── Header: logo hard left, all text right-aligned beside it ─────────────
  // The heading used to sit on its own line below the logo, costing ~14mm of
  // vertical space on an A5 page that has none to spare. Everything textual now
  // shares the logo's band, right-aligned to the margin.
  if (logoDataUrl) {
    doc.addImage(logoDataUrl, 'PNG', contentX, M, LOGO_W, LOGO_H);
  }

  const rightX = pageW - marginR;

  const brandW = scalaLogoWidth(doc, 16, 10);
  const brandY = M + 7;
  drawScalaLogo(doc, rightX - brandW, brandY, 16, 10);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(...GREY);
  doc.text(`Festival Of Original Music ${year}`, rightX, brandY + 6, { align: 'right' });

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...NAVY);
  const heading = `People's Choice Voting Slip: ${heat.heat_name}`;
  doc.text(heading, rightX, brandY + 13, { align: 'right' });
  if (dateShort) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(dateShort, rightX, brandY + 18.5, { align: 'right' });
  }

  // Start below whichever is taller: the logo or the text block.
  let y = Math.max(M + LOGO_H, brandY + 21) + 4;

  // ── Voting table ──────────────────────────────────────────────────────────
  // Breaks appear as their own row so the audience knows when to expect one.
  const breaks = new Set((heat.breaks || []).map(Number));
  const rows = [];
  const breakRowIndexes = [];
  heat.songs.forEach((s, idx) => {
    rows.push([
      String(idx + 1),
      s.artist || s.title || '—',
      s.title  || '—',
      '',  // Vote column — blank for audience
    ]);
    if (breaks.has(idx + 1)) {
      breakRowIndexes.push(rows.length);
      rows.push(['', 'BREAK', '', '']);
    }
  });

  // ── Type size: as large as this particular heat can carry ────────────────
  // These slips are filled in by the audience in a dark venue, so 9pt was hard
  // to read. A flat increase is not safe: the biggest heats run to 16 acts and
  // titles to ~53 characters, which wrap to two lines, and this page has twice
  // before had the table print over the instructions (see the notes below and
  // at the instructions block).
  //
  // So the size is chosen per heat instead of fixed. A 10-act heat gets 14pt
  // and fills the page; a 16-act heat with breaks steps down to 11pt, still
  // well up on the old 9pt. rows.length is used, not heat.songs.length, because
  // break rows take vertical space too.
  // The steps are not guesses: they were measured by rendering this table with
  // the largest real heat (16 acts, titles to 53 chars, which wrap to two
  // lines) and checking the instructions still land above the page foot.
  // 11pt at 18 rows cleared by only 0.8mm, so that step drops straight to 10.
  // Worst case (20 rows) lands on 9pt — never worse than the old flat size.
  const rowCount    = rows.length;
  const bodyFont    = rowCount <= 10 ? 14
                    : rowCount <= 13 ? 13
                    : rowCount <= 16 ? 12
                    : rowCount <= 18 ? 10
                    : 9;
  // Padding shrinks as the font grows, so the extra height goes into legible
  // glyphs rather than whitespace.
  const cellPad     = bodyFont >= 13 ? 1.6 : bodyFont >= 11 ? 1.3 : 1.1;

  autoTable(doc, {
    startY:     y,
    head:       [['#', 'Name', 'Song Title', 'Vote']],
    body:       rows,
    // Was M + 18. The instructions now set at 8pt and wrap to fewer lines, so
    // the reserved strip can be smaller — that reclaimed height is what pays
    // for the larger type above.
    margin:     { left: contentX, right: marginR, bottom: M + 14 },
    tableWidth: contentW,
    styles: {
      fontSize:    bodyFont,
      // Was 3mm, which with 12 acts pushed the table over the instructions.
      cellPadding: cellPad,
      valign:      'middle',
      lineWidth:   0.3,
      lineColor:   [0, 0, 0],
    },
    headStyles: {
      fillColor: NAVY,
      textColor: 255,
      fontStyle: 'bold',
      fontSize:  Math.min(bodyFont, 11),   // header is read once; the acts are not
      lineWidth: 0.3,
      lineColor: [0, 0, 0],
    },
    columnStyles: {
      // 9mm left ~3mm of usable width once padding was removed, so any
      // two-digit position wrapped onto a second line and doubled the row.
      // Trimmed from 13 to 10 (still fits "16" at 14pt) and Vote from 15 to 14,
      // handing the 4mm to Song Title — the column that actually wraps.
      // Name and Song Title are bold: at arm's length in low light that reads
      // as clearly as another point or two of size, and costs no height.
      0: { cellWidth: 10, halign: 'center', fontStyle: 'bold' },
      1: { cellWidth: 42, fontStyle: 'bold' },
      2: { cellWidth: 'auto', fontStyle: 'bold' },
      3: { cellWidth: 14, halign: 'center' },
    },
    didParseCell: (data) => {
      if (data.section === 'body' && breakRowIndexes.includes(data.row.index)) {
        data.cell.styles.fillColor = [254, 243, 199];
        data.cell.styles.textColor = [146, 64, 14];
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.halign    = 'center';
      }
    },
  });

  // ── Voting instructions below table ──────────────────────────────────────
  // Previously clamped with Math.min(finalY + 4, pageH - M - 10), which pulled
  // the text UP into the table whenever the table ran long — printing the
  // instructions straight over the last few acts. Now it always follows the
  // table, and takes a new page only if there is genuinely no room.
  // 8pt, down from 9. These are read once, in the light, before voting starts —
  // unlike the act list, which is read repeatedly in the dark. Giving the point
  // back to the table is the better trade.
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(50, 50, 50);
  const instrLines = doc.splitTextToSize(INSTRUCTIONS, contentW);
  const instrH     = instrLines.length * 3.8;

  let instrY = doc.lastAutoTable.finalY + 5;
  if (instrY + instrH > pageH - M) {
    doc.addPage();
    instrY = M;
  }
  doc.text(instrLines, contentX, instrY);

  doc.save(`${heatFileStem(heat)}-Peoples-Choice-Voting-Slip.pdf`);
  onDone?.();
}

// ---------------------------------------------------------------------------
// Part 5: People's Choice Tally Form PDF
// ---------------------------------------------------------------------------

/**
 * Generate and download the tally form for a heat or the Grand Final.
 *
 * A printed worksheet for counting People's Choice votes by hand at the end of
 * the night. Four columns: SONG NAME | PERFORMER | (tally marks) | TOTAL.
 *
 * Landscape A4 on purpose. This is written on, not read from, so the design
 * priority is the opposite of the voting slip: the tally column takes the
 * majority of the width and rows are as tall as the page allows, because
 * five-bar gates need room. Landscape gives ~150mm of counting space where
 * portrait would give ~60mm.
 *
 * Breaks are omitted — they are not something you count votes for.
 */
export async function downloadTallyFormPdf(heat, { onStart, onDone } = {}) {
  onStart?.();

  const LOGO_W = 18;                       // mm — smaller than the A5 slip
  const LOGO_H = LOGO_W * (783 / 572);     // ≈ 24.6 mm

  let logoDataUrl = null;
  try {
    logoDataUrl = await loadImageDataUrl('/assets/scala-logo.png');
  } catch {
    // Logo unavailable — fall back to text-only header
  }

  const doc   = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();   // 297
  const pageH = doc.internal.pageSize.getHeight();  // 210
  const M     = 10;

  const contentX = M;
  const contentW = pageW - M * 2;   // 277 mm

  const year      = heatYear(heat);
  const dateShort = heat.heat_date
    ? new Date(heat.heat_date + 'T00:00:00Z').toLocaleDateString('en-AU', {
        day: 'numeric', month: 'long', timeZone: 'UTC',
      })
    : '';

  // ── Header: logo hard left, text right-aligned ───────────────────────────
  if (logoDataUrl) {
    doc.addImage(logoDataUrl, 'PNG', contentX, M, LOGO_W, LOGO_H);
  }

  const rightX = pageW - M;
  const brandW = scalaLogoWidth(doc, 16, 10);
  const brandY = M + 6;
  drawScalaLogo(doc, rightX - brandW, brandY, 16, 10);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...NAVY);
  doc.text(`Festival Of Original Music ${year}`, rightX, brandY + 6, { align: 'right' });

  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.text(`People's Choice Tally Form: ${heat.heat_name}`, rightX, brandY + 14, { align: 'right' });
  if (dateShort) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(dateShort, rightX, brandY + 20, { align: 'right' });
  }

  const y = Math.max(M + LOGO_H, brandY + 23) + 4;

  // ── Rows: one per song, breaks excluded ──────────────────────────────────
  const rows = heat.songs.map((s) => [
    s.title  || '—',
    s.artist || '—',
    '',   // tally column — deliberately blank, this is the writing space
    '',   // total column — filled in by hand
  ]);

  // Rows stretch to fill the page: a 4-song Grand Final gets very tall rows,
  // a 16-song heat gets the most it can while still fitting on one page.
  // Capped so a short heat does not produce four absurd half-page rows.
  // Measured, not estimated: the header row renders ~8.9mm at this font, and
  // dividing the space by (rows + 1) as though the header were another body
  // row put a 16-song heat 0.3mm over the page and onto a second sheet.
  const HEAD_H  = 9;   // header row height
  // 6mm, not 2: autoTable renders each row slightly taller than minCellHeight
  // (borders and line-height rounding), which across 16 rows was enough to
  // push the table 0.3mm past the page. Measured, not guessed.
  const SAFETY  = 6;
  // Floor of 8.6mm is not arbitrary: a cell is at least its font height plus
  // 2mm padding top and bottom, so anything lower is ignored and the row grows
  // anyway. Heats beyond ~17 songs will therefore run to a second page — no
  // real heat comes close (the largest is 16).
  const available     = pageH - y - M;
  const rowH          = Math.max(8.6, Math.min(26, (available - HEAD_H - SAFETY) / rows.length));
  // Font steps down as the heat fills up, so a long title still fits the row
  // it was given rather than overflowing it.
  const bodyFont      = rows.length <= 8 ? 16 : rows.length <= 12 ? 15 : 13;
  // Padding, not font size, is what gives way on a full heat. A row can never
  // be shorter than its font height plus padding, so at 16 songs the 2mm
  // padding alone put the table 0.3mm over the page — and shrinking the type
  // is the one thing this form must not do, since it is read in a dark room.
  const cellPad       = rows.length <= 13 ? 2 : 1.2;

  autoTable(doc, {
    startY:     y,
    head:       [['SONG NAME', 'PERFORMER', '', 'TOTAL']],
    body:       rows,
    margin:     { left: contentX, right: M, bottom: M },
    tableWidth: contentW,
    styles: {
      fontSize:      bodyFont,
      cellPadding:   cellPad,
      valign:        'middle',
      lineWidth:     0.4,          // heavier rules than the slip — written on
      lineColor:     [0, 0, 0],
      minCellHeight: rowH,
    },
    headStyles: {
      fillColor: NAVY,
      textColor: 255,
      fontStyle: 'bold',
      fontSize:  Math.min(bodyFont, 12),
      halign:    'center',
      lineWidth: 0.4,
      lineColor: [0, 0, 0],
      minCellHeight: 0,            // header needs no writing room
    },
    columnStyles: {
      // The tally column is 'auto', so it absorbs everything the other three
      // do not use — roughly 150mm of the 277mm available. That is the point
      // of the form.
      //
      // overflow 'ellipsize' on the two text columns is what makes the row
      // heights above real. Left to wrap, a 53-character title (they exist —
      // Heat 3 has one) took three lines at 13pt and pushed a 16-song heat
      // onto a second page, which is useless for something you count on.
      // One line, clipped, at a slightly smaller size: the counter is matching
      // a name they already have on the voting slip, not reading it cold.
      0: { cellWidth: 58, fontStyle: 'bold', fontSize: Math.min(bodyFont, 11), overflow: 'ellipsize' },
      1: { cellWidth: 45, fontSize: Math.min(bodyFont, 11), overflow: 'ellipsize' },
      2: { cellWidth: 'auto' },
      3: { cellWidth: 26, halign: 'center' },
    },
  });

  doc.save(`${heatFileStem(heat)}-Peoples-Choice-Tally-Form.pdf`);
  onDone?.();
}
